import { Table } from 'antd';
import { type Dispatch, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type BankModuleSpecsFragment } from '../../../../api/fragments/BankModuleSpecs';
import { type WebCalcSettingDataFragment } from '../../../../api/fragments/WebCalcSettingData';
import { useListWebCalcSettingsQuery } from '../../../../api/queries/listWebCalcSettings';
import PaginatedTable, { type PageAction, type PageState } from '../../../../components/PaginatedTable';
import { useListReducer } from '../../../../components/useListReducer';
import { type PermissionValues } from '../../../../utilities/hasPermissions';
import { type OpenRemoteFPSettingDrawer, type SetOnRemoteFPSettingSubmitted } from './RemoteFPSettingDrawer';
import RemoteFPSettingEmptyList from './RemoteFPSettingEmptyList';

type BankModuleTableInnerProps = {
    openDrawer: OpenRemoteFPSettingDrawer;
    dataSource: WebCalcSettingDataFragment[];
    state: PageState;
    dispatch: Dispatch<PageAction>;
    total: number;
};
const RemoteFPSettingListInner = ({ openDrawer, dataSource, state, dispatch, total }: BankModuleTableInnerProps) => {
    const { t } = useTranslation(['bankModuleDetails']);

    return (
        <PaginatedTable
            dataSource={dataSource}
            dispatch={dispatch}
            onRow={value => ({
                onClick: () => {
                    openDrawer(value);
                },
            })}
            rowKey="id"
            state={state}
            tableName={t('bankModuleDetails:tabs.remoteFpSettingList')}
            total={total}
        >
            <Table.Column
                key="displayName"
                dataIndex="displayName"
                title={t('bankModuleDetails:remoteFpSetting.columns.displayName')}
            />
            <Table.Column
                key="domain"
                dataIndex="domain"
                title={t('bankModuleDetails:remoteFpSetting.columns.domain')}
            />
            <Table.Column
                key="calculationUrl"
                dataIndex="calculationUrl"
                title={t('bankModuleDetails:remoteFpSetting.columns.calculationUrl')}
            />
        </PaginatedTable>
    );
};

export type RemoteFPSettingListProps = {
    module: BankModuleSpecsFragment;
    openDrawer: OpenRemoteFPSettingDrawer;
    setOnRemoteFPSettingSubmitted: SetOnRemoteFPSettingSubmitted;
    permissions: Pick<PermissionValues, 'hasUpdatePermission'>;
};
const RemoteFPSettingList = ({
    module,
    openDrawer,
    setOnRemoteFPSettingSubmitted,
    permissions,
}: RemoteFPSettingListProps) => {
    const [state, dispatch] = useListReducer();
    const { page, pageSize } = state;

    const { data, refetch } = useListWebCalcSettingsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            pagination: { offset: (page - 1) * pageSize, limit: pageSize },
            moduleId: module.id,
        },
    });

    useEffect(() => {
        setOnRemoteFPSettingSubmitted(() => () => {
            refetch();
        });
    }, [refetch, setOnRemoteFPSettingSubmitted]);

    const dataSource = useMemo(() => data?.result?.items ?? [], [data?.result?.items]);
    const total = data?.result?.count ?? 0;

    return total === 0 ? (
        <RemoteFPSettingEmptyList openDrawer={openDrawer} permissions={permissions} />
    ) : (
        <RemoteFPSettingListInner
            dataSource={dataSource}
            dispatch={dispatch}
            openDrawer={openDrawer}
            state={state}
            total={total}
        />
    );
};

export default RemoteFPSettingList;
